import { useNavigate, useSearchParams } from "react-router-dom";
import CloseIcon from "./../../assets/icons/close.svg?react";
import CheckIcon from "./../..//assets/icons/check.svg?react";
import { AiDeploysUrl } from "../../utils/urls";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { AiAppCreateSchema } from "../../utils/validations";
import {
  selectInstancesRegions,
  selectInstancesRegionsLoading,
  getRegionsAsync,
} from "../../store/instances/instancesSlice";
import {
  getDBSCatalogsAsync,
  selectDBSCatalogs,
  selectDBSCatalogsLoading,
} from "../../store/database/servicesSlice";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import {
  Alert,
  Button,
  cn,
  Flex,
  Input,
  Loading,
  Typography,
  uuid,
} from "djuno-design";
import useLimitations from "../../hooks/useLimitations";
import { DBSCatalog } from "../../types/database";
import {
  getAiRegionsDataAsync,
  getAiResourceFlavorsAsync,
  selectAiRegionsData,
  selectAiRegionsDataLoading,
  selectAiResourceFlavors,
} from "../../store/ai/notebooks/notebooksSlice";
import {
  AiServiceAttachRepoInput,
  AiServiceConfigInput,
  AiServiceFlavorInput,
  AiServiceRegionInput,
} from "../../components/ai-notebooks/create/components";
import { datacenterDetails } from "../../utils/countries";
import {
  AiAppDeployType,
  AiAppScallingStrategy,
  AiResourceUseCaseType,
} from "../../types/ai";
import {
  createAiAppAsync,
  getAiAppImagesAsync,
  // getAiAppPartnersAsync,
  selectAiAppImages,
  selectAiApps,
  selectAiAppsActionLoading,
  // selectAiappsLoading,
} from "../../store/ai/deploys/appsSlice";
import {
  AiAppDeployTypesInput,
  AiAppReadinessProbeInput,
  AiAppScallingStrategyInput,
} from "../../components/ai-deploys/create/components";
import { capitalizeFirstLetter } from "../../utils";
import { currency } from "../../utils/currency";
import { useScrollToElement } from "../../hooks/useScrollToElement";

type CreateStep =
  | "region"
  | "application"
  | "resources"
  | "attachContainers"
  | "configuration"
  | "review";

const createSteps: Array<CreateStep> = [
  "region",
  "application",
  "resources",
  "attachContainers",
  "configuration",
  "review",
];

export type CreateAppFormType = {
  selectedRegionDataId: string;

  selectedImage?: string | null;
  selectedImageTag?: string | null;
  dockerImage?: string | null;
  dockerRunCommand?: string | null;

  serviceName: string;
  labels?: Array<{ key: string; value: string }>;
  replicas?: number | null;
  averageUsageTarget?: number | null;
  replicasMax?: number | null;
  replicasMin?: number | null;
  resourceType?: string | null;

  gitRepos?: Array<{
    url: string;
    mountPath: string;
    permission: string;
    cache: boolean;
  }>;

  selectedResourceId: string;
  selectedResourceUnit: number;
  defaultHttpPort?: number | null;
  readinessProbePath?: string | null;
  readinessProbePort?: number | null;
};

const AiAppCreatePage = () => {
  const [query] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const apps = useAppSelector(selectAiApps);
  // const appsLoading = useAppSelector(selectAiappsLoading);

  const regions = useAppSelector(selectInstancesRegions);
  const regionsLoading = useAppSelector(selectInstancesRegionsLoading);
  const regionsData = useAppSelector(selectAiRegionsData);
  const regionsDataLoading = useAppSelector(selectAiRegionsDataLoading);

  const images = useAppSelector(selectAiAppImages);

  const flavors = useAppSelector(selectAiResourceFlavors);

  const catalogs = useAppSelector(selectDBSCatalogs);
  const catalogsLoading = useAppSelector(selectDBSCatalogsLoading);

  const actionLoading = useAppSelector(selectAiAppsActionLoading);

  // states
  const [expanded, setExpanded] = useState<CreateStep>("region");
  const [selectedDeployType, setSelectedDeployType] =
    useState<AiAppDeployType>("docker-image");
  const [selectedUseCase, setSelectedUseCase] =
    useState<AiResourceUseCaseType>("cpu");
  const [scallingStrategy, setScallingStrategy] =
    useState<AiAppScallingStrategy>("fixed");
  const [readinessProbe, setReadinessProbe] = useState<boolean>(false);

  const [filteredCatalogs, setFilteredCatalogs] = useState<Array<DBSCatalog>>(
    []
  );

  //data for create from backup
  const returnUrl = query.get("returnUrl");

  const methods = useForm<CreateAppFormType>({
    resolver: yupResolver(
      AiAppCreateSchema(selectedDeployType, scallingStrategy, readinessProbe)
    ),
    mode: "all",
    defaultValues: {
      selectedRegionDataId: "",

      selectedImage: "",
      selectedImageTag: "",
      dockerImage: "",
      dockerRunCommand: "",

      selectedResourceId: "",
      selectedResourceUnit: 1,
      replicas: 1,
      averageUsageTarget: 75,
      replicasMax: 1,
      replicasMin: 1,
      resourceType: "CPU",

      gitRepos: undefined,

      serviceName: "",
      labels: undefined,
      defaultHttpPort: 8080,
      readinessProbePath: "",
      readinessProbePort: 8080,
    },
  });

  const {
    setValue,
    handleSubmit,
    control,
    formState: { errors, isValid },
    trigger,
    watch,
    register,
  } = methods;

  // watch form values
  const selectedRegionDataId = watch("selectedRegionDataId");

  const selectedImageId = watch("selectedImage");
  const selectedImageTag = watch("selectedImageTag");
  const dockerImage = watch("dockerImage");
  const dockerRunCommand = watch("dockerRunCommand");

  const selectedResourceId = watch("selectedResourceId");
  const selectedResourceUnit = watch("selectedResourceUnit");
  const replicas = watch("replicas");
  const replicasMin = watch("replicasMin");
  const replicasMax = watch("replicasMax");
  const resourceType = watch("resourceType");
  const averageUsageTarget = watch("averageUsageTarget");

  const attachedGitRepos = watch("gitRepos");

  const appName = watch("serviceName");
  const appLabels = watch("labels");
  const defaultHttpPort = watch("defaultHttpPort");
  const readinessProbePath = watch("readinessProbePath");
  const readinessProbePort = watch("readinessProbePort");

  useEffect(() => {
    trigger("labels");
  }, [appLabels, trigger]);

  useEffect(() => {
    trigger("readinessProbePath");
    trigger("readinessProbePort");
  }, [readinessProbe, trigger]);

  //get first data
  useEffect(() => {
    dispatch(getRegionsAsync());
    dispatch(getAiRegionsDataAsync({}));
    dispatch(getDBSCatalogsAsync("ai-app"));
  }, [dispatch]);

  // get partners based on selected region
  useEffect(() => {
    if (selectedRegionDataId && selectedRegionDataId !== "")
      dispatch(getAiAppImagesAsync({ region: selectedRegionDataId }));
  }, [dispatch, selectedRegionDataId]);

  // get partners based on selected region
  // useEffect(() => {
  //   if (selectedRegionDataId && selectedRegionDataId !== "")
  //     dispatch(getAiAppPartnersAsync({ region: selectedRegionDataId }));
  // }, [dispatch, selectedRegionDataId]);

  // get resource (flavors) based on selected region
  useEffect(() => {
    if (selectedRegionDataId && selectedRegionDataId !== "")
      dispatch(getAiResourceFlavorsAsync({ region: selectedRegionDataId }));
  }, [dispatch, selectedRegionDataId]);

  useEffect(() => {
    setValue("serviceName", "ai-app" + "-" + uuid(6));
  }, [setValue]);

  // find selected region data
  const selectedRegion = useMemo(() => {
    return regions.find((r) => r.datacenterLocation === selectedRegionDataId);
  }, [regions, selectedRegionDataId]);

  // find selected datacenter
  const selectedDatacenter = useMemo(() => {
    return datacenterDetails.find((dc) => dc.location === selectedRegionDataId);
  }, [selectedRegionDataId]);

  // find selected image data
  const selectedImage = useMemo(() => {
    return images.find((i) => i.id === selectedImageId);
  }, [images, selectedImageId]);

  // find selected freamwork data
  const selectedResource = useMemo(() => {
    return flavors.find((f) => f.id === selectedResourceId);
  }, [flavors, selectedResourceId]);

  // filter addons by selected engine and period
  useEffect(() => {
    if (catalogs) {
      const filtered = catalogs.filter((catalog) =>
        catalog.product?.startsWith("publiccloud-ai-")
      );
      setFilteredCatalogs(filtered);
    }
  }, [catalogs]);

  const catalog = useMemo(() => {
    return filteredCatalogs.find((c) =>
      c.planCode.includes(selectedResourceId + ".minute.consumption")
    );
  }, [filteredCatalogs, selectedResourceId]);

  const partnerCatalog = useMemo(() => {
    return filteredCatalogs.find((c) => {
      return selectedImage?.partnerId === "voxist"
        ? c.planCode.match(selectedImage.id + "-cpu-bracket1.unit.consumption")
        : c.planCode.match(
            selectedImage?.partnerId +
              "-" +
              selectedImage?.id +
              "-cpu.minute.consumption"
          );
    });
  }, [filteredCatalogs, selectedImage]);

  const handleEditRegionSelection = () => {
    setExpanded("region");
  };

  const handleEditApplicationSelection = () => {
    setExpanded("application");
  };

  const handleEditResourcesSelection = () => {
    setExpanded("resources");
  };

  const handleEditAttachContainersSelection = () => {
    setExpanded("attachContainers");
  };

  const handleEditConfigSelection = () => {
    setExpanded("configuration");
  };

  const handleSubmitForm = useCallback(() => {
    if (!selectedResource) return;

    //http port
    let httpPort = 8080;
    if (selectedDeployType === "docker-image") {
      httpPort = defaultHttpPort || 8080;
    }

    const labels: Record<string, string> = (appLabels || [])
      .filter(({ key, value }) => key && value)
      .reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {} as Record<string, string>);

    if (Object.keys(labels).length === 0) {
      labels["default"] = "true";
    }

    // calc resource data
    let resources: any = {
      flavor: selectedResource.id,
      memory: selectedResource.resourcesPerUnit.memory,
      publicNetwork: selectedResource.resourcesPerUnit.publicNetwork,
      privateNetwork: 0,
      ephemeralStorage: selectedResource.resourcesPerUnit.ephemeralStorage,
    };

    //for cpu types
    if (selectedUseCase === "cpu")
      resources = {
        ...resources,
        cpu: selectedResourceUnit,
      };

    //for gpu types
    if (selectedUseCase === "gpu")
      resources = {
        ...resources,
        gpu: selectedResourceUnit,
        gpuBrand: selectedResource.gpuInformation?.gpuBrand,
        gpuModel: selectedResource.gpuInformation?.gpuModel,
        gpuMemory: selectedResource.gpuInformation?.gpuMemory,
      };

    //image an partner
    let image = dockerImage;
    let partnerId: string | null = null;
    if (selectedDeployType === "partner") {
      image = selectedImageId + ":" + selectedImageTag;
      partnerId = selectedImage?.partnerId || null;
    }

    //scaling strategy
    let scaling: any = {};
    if (scallingStrategy === "fixed") {
      scaling = {
        fixed: { replicas },
      };
    }
    if (scallingStrategy === "automatic") {
      scaling = {
        automatic: {
          averageUsageTarget,
          replicasMax,
          replicasMin,
          resourceType,
        },
      };
    }

    //probe
    let probe: { path: string; port: number } | null = null;
    if (readinessProbe && readinessProbePath && readinessProbePort) {
      probe = { path: readinessProbePath, port: Number(readinessProbePort) };
    }

    //volumes
    let volumes: any[] = [];
    if (selectedDeployType === "docker-image") {
      volumes = (attachedGitRepos || []).map((repo) => ({
        mountPath: repo.mountPath,
        permission: repo.permission,
        publicGit: {
          url: repo.url,
        },
        cache: repo.cache || false,
      }));
    }

    const apiData = {
      command:
        dockerRunCommand && dockerRunCommand !== "" ? [dockerRunCommand] : [],
      defaultHttpPort: Number(httpPort),
      image,
      labels,
      name: appName,
      partnerId,
      probe,
      region: selectedRegionDataId,
      resources,
      scalingStrategy: scaling,
      unsecureHttp: false,
      volumes,
    };

    // console.log(apiData);
    dispatch(createAiAppAsync({ data: apiData })).then((action) => {
      if (action.type === "ai-apps/create/fulfilled") {
        navigate(returnUrl || AiDeploysUrl);
      }
    });
  }, [
    selectedResource,
    selectedDeployType,
    appLabels,
    selectedUseCase,
    selectedResourceUnit,
    dockerImage,
    scallingStrategy,
    readinessProbe,
    dockerRunCommand,
    appName,
    selectedRegionDataId,
    dispatch,
    defaultHttpPort,
    selectedImageId,
    selectedImageTag,
    selectedImage?.partnerId,
    replicas,
    averageUsageTarget,
    replicasMax,
    replicasMin,
    resourceType,
    readinessProbePath,
    readinessProbePort,
    attachedGitRepos,
    navigate,
    returnUrl,
  ]);

  const { numberOfAiAppsLimit, numberOfAiAppsLimitChecker } = useLimitations({
    cases: ["NumberOfAiApps"],
  });

  const isExceedNumberOfAiApps = useMemo(() => {
    return numberOfAiAppsLimitChecker(apps.length);
  }, [apps, numberOfAiAppsLimitChecker]);

  useScrollToElement({ targetElementId: expanded });

  return (
    <>
      <div className="flex items-center justify-between h-16 px-6 sticky top-0 z-20 bg-white dark:bg-dark-1 border-b dark:border-dark-2">
        <div className="items-center justify-between flex flex-1 transition duration-150">
          <Typography.Text className="font-medium">
            Deploy an app
          </Typography.Text>
        </div>
        <div className="">
          <Button
            uiType="light"
            uiSize="small"
            onClick={() => navigate(returnUrl || AiDeploysUrl)}
            className="group"
          >
            <CloseIcon className="w-3 h-3 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
          </Button>
        </div>
      </div>

      {catalogsLoading && (
        <Flex
          items="center"
          justify="center"
          className="h-full w-full min-h-[calc(100vh-128px)]"
        >
          <Loading borderSize={2} />
        </Flex>
      )}
      {!catalogsLoading && (
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <div className="mt-10 w-full px-6 flex flex-col lg:flex-row pb-24 gap-8 relative">
            <div className="w-full flex flex-col">
              {/* select region */}
              <Accordion
                expanded={expanded === "region"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="region"
                  sx={{ my: 0 }}
                >
                  <div className="flex w-full items-center justify-between">
                    {createSteps.indexOf(expanded) > 0 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Location selected: {selectedRegionDataId}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditRegionSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "region",
                          }
                        )}
                      >
                        1. Datacenter location
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3">
                    <AiServiceRegionInput
                      regions={regions}
                      regionsData={regionsData}
                      control={control}
                      loading={regionsLoading || regionsDataLoading}
                      errorMessage={errors.selectedRegionDataId?.message}
                    />
                  </div>
                  <Button
                    id="2th-next-button"
                    disabled={selectedRegionDataId === ""}
                    onClick={() => setExpanded("application")}
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* select application */}
              <Accordion
                expanded={expanded === "application"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="application"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 1 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          {selectedDeployType === "partner" && (
                            <>
                              Partner:{" "}
                              {capitalizeFirstLetter(selectedImage?.partnerId)}{" "}
                              /Image: {selectedImageId}:{selectedImageTag}
                            </>
                          )}
                          {selectedDeployType === "docker-image" && (
                            <>Image: {dockerImage}</>
                          )}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditApplicationSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "resources",
                          }
                        )}
                      >
                        2. Application to deploy
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <AiAppDeployTypesInput
                      control={control}
                      selectedDeployType={selectedDeployType}
                      setSelectedDeployType={setSelectedDeployType}
                      catalogs={filteredCatalogs}
                      catalogsLoading={catalogsLoading}
                      errors={errors}
                    />
                  </div>

                  <Button
                    disabled={
                      selectedDeployType === "docker-image"
                        ? dockerImage === ""
                        : selectedImageId === ""
                    }
                    onClick={() => setExpanded("resources")}
                    id="2th-next-button"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* select resources */}
              <Accordion
                expanded={expanded === "resources"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="resources"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) > 2 ? (
                      <Flex
                        direction={{ default: "col", md: "row" }}
                        justify={{ default: "between" }}
                        className="gap-4 w-full"
                      >
                        <Typography.Text className="!text-base font-medium flex items-center gap-1">
                          <CheckIcon className="w-5 h-5 text-primary-500" />
                          Resource selected: {selectedResourceId} - x
                          {selectedResourceUnit}
                        </Typography.Text>
                        <Typography.Link
                          className="!text-sm"
                          onClick={handleEditResourcesSelection}
                        >
                          edit this step
                        </Typography.Link>
                      </Flex>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "resources",
                          }
                        )}
                      >
                        3. Resources
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <Flex direction="col" className="mt-3 mb-5 gap-5 w-full">
                    <AiServiceFlavorInput
                      control={control}
                      selectedUseCase={selectedUseCase}
                      setSelectedUseCase={setSelectedUseCase}
                      catalogs={filteredCatalogs}
                      catalogsLoading={catalogsLoading}
                      errorMessage={errors.selectedResourceId?.message}
                      useCasePrefix="AI Deploy"
                    />
                    <AiAppScallingStrategyInput
                      control={control}
                      scallingStrategy={scallingStrategy}
                      setScallingStrategy={setScallingStrategy}
                      errors={errors}
                    />
                    {catalog && (
                      <Typography.Text className="!text-sm">
                        Price for selected resources:{" "}
                        {currency(
                          selectedResourceUnit *
                            catalog.pricings[0].price *
                            60 *
                            (scallingStrategy === "fixed"
                              ? replicas || 1
                              : replicasMin || 1),
                          {
                            minimumFractionDigits: 3,
                          }
                        )}
                        /hour
                      </Typography.Text>
                    )}
                    <Alert uiType="info" showIcon>
                      <Typography.Text size="xs">
                        The price displayed above applies to the physical
                        resources for deployment. Additional fees may apply
                        depending on the partner model chosen. Please refer to
                        the summary.
                      </Typography.Text>
                    </Alert>
                  </Flex>

                  <Button
                    disabled={
                      !selectedResourceId ||
                      selectedResourceId === "" ||
                      (scallingStrategy === "automatic" &&
                        Number(replicasMin) > Number(replicasMax))
                    }
                    onClick={() =>
                      setExpanded(
                        selectedDeployType === "docker-image"
                          ? "attachContainers"
                          : "configuration"
                      )
                    }
                    id="3th-next-button"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* attach Containers */}
              {selectedDeployType === "docker-image" && (
                <Accordion
                  expanded={expanded === "attachContainers"}
                  sx={{ boxShadow: "none" }}
                  className="bg-white dark:bg-dark-3"
                >
                  <AccordionSummary
                    expandIcon={null}
                    aria-controls="panel1bh-content"
                    id="attachContainers"
                    sx={{ my: 0 }}
                  >
                    <div className="w-full flex">
                      {createSteps.indexOf(expanded) > 3 ? (
                        <Flex
                          direction={{ default: "col", md: "row" }}
                          justify={{ default: "between" }}
                          className="gap-4 w-full"
                        >
                          <Typography.Text className="!text-base font-medium flex items-center gap-1">
                            <CheckIcon className="w-5 h-5 text-primary-500" />
                            Containers attached: {attachedGitRepos?.length ||
                              0}{" "}
                            volumes attached
                          </Typography.Text>
                          <Typography.Link
                            className="!text-sm"
                            onClick={handleEditAttachContainersSelection}
                          >
                            edit this step
                          </Typography.Link>
                        </Flex>
                      ) : (
                        <Typography.Text
                          className={cn(
                            "!text-base font-medium flex items-center gap-1",
                            {
                              "!text-primary-500":
                                expanded === "attachContainers",
                            }
                          )}
                        >
                          4. Attach a container or a Git repository
                        </Typography.Text>
                      )}
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className="mt-3 mb-5">
                      <AiServiceAttachRepoInput
                        control={control}
                        register={register}
                        errors={errors}
                        gitCache={true}
                      />
                    </div>

                    <Button
                      disabled={
                        errors.gitRepos &&
                        errors.gitRepos.length !== undefined &&
                        errors.gitRepos.length > 0
                      }
                      onClick={() => setExpanded("configuration")}
                      id="attachContainers"
                    >
                      Next
                    </Button>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* select configs */}
              <Accordion
                expanded={expanded === "configuration"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="configuration"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) >
                    (selectedDeployType === "docker-image" ? 4 : 3) ? (
                      <div className="flex flex-col gap-5 w-full">
                        <Flex
                          direction={{ default: "col", md: "row" }}
                          justify={{ default: "between" }}
                          className="gap-4 w-full"
                        >
                          <Typography.Text className="!text-base font-medium flex items-center gap-1">
                            <CheckIcon className="w-5 h-5 text-primary-500" />
                            App name: {appName}{" "}
                            <Typography.Text size="xs" uiType="secondary">
                              ({appLabels?.length || 0} labels)
                            </Typography.Text>
                          </Typography.Text>
                          <Typography.Link
                            className="!text-sm"
                            onClick={handleEditConfigSelection}
                          >
                            edit this step
                          </Typography.Link>
                        </Flex>
                      </div>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "configuration",
                          }
                        )}
                      >
                        {selectedDeployType === "docker-image"
                          ? "5. Configure your app "
                          : "4. Configure your app"}
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <Flex direction="col" className="mt-3 mb-5 gap-5 w-full">
                    <AiServiceConfigInput
                      control={control}
                      register={register}
                      errors={errors}
                      serviceNameInputLabel="Your app name"
                    />

                    {selectedDeployType === "docker-image" && (
                      <Flex direction="col" className="gap-1 w-full">
                        <Controller
                          control={control}
                          name="defaultHttpPort"
                          render={({ field: { value, onChange } }) => (
                            <Input
                              label="HTTP port"
                              value={value || 8080}
                              onChange={onChange}
                              type="number"
                            />
                          )}
                        />
                        <Typography.Text size="xs">
                          The HTTP port your app is linked to.
                        </Typography.Text>
                      </Flex>
                    )}

                    <AiAppReadinessProbeInput
                      control={control}
                      readinessProbe={readinessProbe}
                      setReadinessProbe={setReadinessProbe}
                      errors={errors}
                    />
                  </Flex>

                  <Button
                    disabled={
                      !appName ||
                      appName === "" ||
                      errors.serviceName !== undefined ||
                      (errors.labels &&
                        errors.labels.length !== undefined &&
                        errors.labels.length > 0)
                    }
                    onClick={() => setExpanded("review")}
                    id="4th-next-button"
                  >
                    Next
                  </Button>
                </AccordionDetails>
              </Accordion>

              {/* review */}
              <Accordion
                expanded={expanded === "review"}
                sx={{ boxShadow: "none" }}
                className="bg-white dark:bg-dark-3"
              >
                <AccordionSummary
                  expandIcon={null}
                  aria-controls="panel1bh-content"
                  id="review"
                  sx={{ my: 0 }}
                >
                  <div className="w-full flex">
                    {createSteps.indexOf(expanded) >
                    (selectedDeployType === "docker-image" ? 5 : 4) ? (
                      <div className="flex flex-col gap-5 w-full">
                        <div className="flex w-full items-center justify-between">
                          <Typography.Text className="!text-base font-medium flex items-center gap-1">
                            <CheckIcon className="w-5 h-5 text-primary-500" />
                            Review
                          </Typography.Text>
                        </div>
                      </div>
                    ) : (
                      <Typography.Text
                        className={cn(
                          "!text-base font-medium flex items-center gap-1",
                          {
                            "!text-primary-500": expanded === "review",
                          }
                        )}
                      >
                        {selectedDeployType === "docker-image"
                          ? "6. Review "
                          : "5. Review"}
                      </Typography.Text>
                    )}
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="mt-3 mb-5">
                    <Flex direction="col" className="w-full gap-5 px-5">
                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">Image</Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedDeployType === "partner" && (
                            <>
                              {selectedImage?.partnerId}/{selectedImageId}:
                              {selectedImageTag}
                            </>
                          )}
                          {selectedDeployType === "docker-image" && (
                            <>{dockerImage}</>
                          )}
                        </Typography.Text>
                      </Flex>

                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">Region</Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedRegion ? (
                            <>
                              {selectedRegion.countryCode.toUpperCase()} -{" "}
                              {selectedDatacenter?.title} ({selectedRegion.name}
                              )
                            </>
                          ) : (
                            <> {selectedRegionDataId}</>
                          )}
                        </Typography.Text>
                      </Flex>

                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">
                          Model and number of resources per replica
                        </Typography.Text>
                        <Typography.Text size="sm" uiType="secondary">
                          {selectedResourceId} - x{selectedResourceUnit}
                        </Typography.Text>
                      </Flex>

                      {readinessProbe && (
                        <Flex direction="col" className="w-full">
                          <Typography.Text size="sm">
                            Readiness probe
                          </Typography.Text>
                          <Typography.Text size="sm" uiType="secondary">
                            {readinessProbePath}:{readinessProbePort}
                          </Typography.Text>
                        </Flex>
                      )}

                      <Flex direction="col" className="w-full">
                        <Typography.Text size="sm">Scaling</Typography.Text>
                        {scallingStrategy === "automatic" && (
                          <Typography.Text size="sm" uiType="secondary">
                            Auto-scaling
                            <ul className="list-disc marker:text-primary-400 pl-8 space-y-1 list-image-[#eee]">
                              <li>Minimum number of replicas: {replicasMin}</li>
                              <li>Maximum number of replicas: {replicasMax}</li>
                              <li>Metric monitored: {resourceType}</li>
                              <li>Trigger threshold: {averageUsageTarget}%</li>
                            </ul>
                          </Typography.Text>
                        )}
                        {scallingStrategy === "fixed" && (
                          <Typography.Text size="sm" uiType="secondary">
                            Fixed scaling
                            <ul className="list-disc marker:text-primary-400 pl-8 space-y-1 list-image-[#eee]">
                              <li>Replicas: {replicas}</li>
                            </ul>
                          </Typography.Text>
                        )}
                      </Flex>
                      {catalog && (
                        <Flex direction="col" className="w-full">
                          <Typography.Text size="sm">
                            Price for selected resources
                          </Typography.Text>
                          <Typography.Text size="sm" uiType="secondary">
                            {currency(
                              selectedResourceUnit *
                                catalog.pricings[0].price *
                                60 *
                                (scallingStrategy === "fixed"
                                  ? replicas || 1
                                  : replicasMin || 1),
                              {
                                minimumFractionDigits: 3,
                              }
                            )}
                            /hour
                          </Typography.Text>
                        </Flex>
                      )}

                      {selectedDeployType === "partner" && partnerCatalog && (
                        <Flex direction="col" className="w-full">
                          <Typography.Text size="sm">
                            Partner licence price{" "}
                            {capitalizeFirstLetter(selectedImage?.partnerId)}
                          </Typography.Text>
                          <Typography.Text size="sm" uiType="secondary">
                            {currency(
                              selectedResourceUnit *
                                partnerCatalog.pricings[0].price *
                                60 *
                                (selectedImage?.partnerId === "voxist"
                                  ? 60
                                  : 1) *
                                (scallingStrategy === "fixed"
                                  ? replicas || 1
                                  : replicasMin || 1),
                              {
                                minimumFractionDigits: 3,
                              }
                            )}
                            /hour
                          </Typography.Text>
                        </Flex>
                      )}
                      {catalog && (
                        <Flex direction="col" className="w-full">
                          <Typography.Text size="sm">
                            Total cost
                          </Typography.Text>
                          <Typography.Text size="sm" uiType="secondary">
                            {currency(
                              (partnerCatalog
                                ? selectedResourceUnit *
                                  partnerCatalog.pricings[0].price *
                                  60 *
                                  (selectedImage?.partnerId === "voxist"
                                    ? 60
                                    : 1) *
                                  (scallingStrategy === "fixed"
                                    ? replicas || 1
                                    : replicasMin || 1)
                                : 0) +
                                selectedResourceUnit *
                                  catalog.pricings[0].price *
                                  60 *
                                  (scallingStrategy === "fixed"
                                    ? replicas || 1
                                    : replicasMin || 1),
                              {
                                minimumFractionDigits: 3,
                              }
                            )}
                            /hour
                          </Typography.Text>
                        </Flex>
                      )}
                    </Flex>
                  </div>

                  <Flex>
                    <Button
                      type="submit"
                      disabled={
                        !isValid || isExceedNumberOfAiApps || actionLoading
                      }
                      uiType="primary"
                      loading={actionLoading}
                      tooltip={{
                        content: isExceedNumberOfAiApps
                          ? numberOfAiAppsLimit?.LimitationMessage
                          : "",
                        className: "!text-sm",
                      }}
                    >
                      Deploy an app
                    </Button>
                  </Flex>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </form>
      )}
    </>
  );
};

export default AiAppCreatePage;
